import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createApiCall } from 'services'

/**
 * VERA-362 — Settings → Features "Custom email sending domain" + "SMS number"
 * cards. Reads/writes the same `companies_settings` fields the API's
 * `CompanySettingsDto` returns from `GET /companies/admin/:companyId/settings`.
 */

export type EmailDomainStatus = 'pending' | 'verified' | 'failed'

export type EmailDomainDnsRecord = {
  type: string
  host: string
  data: string
  valid?: boolean
}

export type CompanySettingsResponse = {
  company_id: string
  email_sending_domain: string | null
  email_domain_status: EmailDomainStatus | null
  email_domain_dns: Record<string, EmailDomainDnsRecord> | null
  link_host: string | null
  sms_number: string | null
  sms_campaign_approved: boolean
}

const COMPANY_SETTINGS_QUERY_KEY = (companyId: string) => ['companySettings', companyId]

const getCompanySettings = async (companyId: string): Promise<CompanySettingsResponse> => {
  const response = await createApiCall({
    method: 'GET',
    url: `/companies/admin/${companyId}/settings`,
  })
  return response.data as CompanySettingsResponse
}

export const useCompanySettings = (companyId?: string) =>
  useQuery<CompanySettingsResponse>({
    queryKey: COMPANY_SETTINGS_QUERY_KEY(companyId ?? ''),
    queryFn: () => getCompanySettings(companyId as string),
    enabled: !!companyId,
  })

export const useAddEmailSendingDomain = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ companyId, domain }: { companyId: string; domain: string }) => {
      const response = await createApiCall({
        method: 'POST',
        url: '/settings/brand/email-domain',
        data: { domain },
      })
      return response.data as CompanySettingsResponse
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: COMPANY_SETTINGS_QUERY_KEY(variables.companyId) })
    },
  })
}

export const useVerifyEmailSendingDomain = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ companyId }: { companyId: string }) => {
      const response = await createApiCall({
        method: 'POST',
        url: '/settings/brand/email-domain/verify',
      })
      return response.data as CompanySettingsResponse
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: COMPANY_SETTINGS_QUERY_KEY(variables.companyId) })
    },
  })
}

export const useRemoveEmailSendingDomain = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ companyId }: { companyId: string }) => {
      const response = await createApiCall({
        method: 'DELETE',
        url: '/settings/brand/email-domain',
      })
      return response.data as CompanySettingsResponse
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: COMPANY_SETTINGS_QUERY_KEY(variables.companyId) })
    },
  })
}
